import { supabase } from '../lib/supabase';

const SITE_URL = 'https://site-proof.app';

/**
 * Error-surfacing pattern: see canonical doc-comment at the top of
 * src/services/clientService.js. Services preserve the real error object
 * (with code/message/details/hint) and page-layer callers branch on
 * `err?.code` for known cases.
 *
 * Auth-specific note: some user-facing strings are intentionally generic
 * for security reasons (e.g. login failures should not reveal whether the
 * email exists vs the password is wrong — that would enable user
 * enumeration). The pattern still applies internally — log the real error
 * and return it — but callers may render generic UI copy on purpose.
 * See LoginForm for the precedent.
 */
function surfaceError(scope, error) {
  console.error(`[authService] ${scope} failed:`, {
    code: error?.code,
    status: error?.status,
    message: error?.message,
  });
  return error;
}

export const authService = {
  async signIn(email, password) {
    try {
      const { data, error } = await supabase?.auth?.signInWithPassword({
        email,
        password
      });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error: surfaceError('signIn', error) };
    }
  },

  async signUp(email, password, fullName, role = 'worker', phone = '') {
    try {
      const { data, error } = await supabase?.auth?.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${SITE_URL}/auth/callback`,
          data: {
            full_name: fullName,
            role: role,
            phone: phone
          }
        }
      });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error: surfaceError('signUp', error) };
    }
  },

  async resendVerificationEmail(email) {
    try {
      const { error } = await supabase?.auth?.resend({
        type: 'signup',
        email,
        options: {
          emailRedirectTo: `${SITE_URL}/auth/callback`
        }
      });
      if (error) throw error;
      return { error: null };
    } catch (error) {
      return { error: surfaceError('resendVerificationEmail', error) };
    }
  },

  async forgotPassword(email) {
    try {
      const { error } = await supabase?.auth?.resetPasswordForEmail(email, {
        redirectTo: `${SITE_URL}/reset-password`
      });
      if (error) throw error;
      return { error: null };
    } catch (error) {
      return { error: surfaceError('forgotPassword', error) };
    }
  },

  async updatePassword(newPassword) {
    try {
      const { data, error } = await supabase?.auth?.updateUser({
        password: newPassword
      });
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error: surfaceError('updatePassword', error) };
    }
  },

  async signOut() {
    try {
      const { error } = await supabase?.auth?.signOut();
      if (error) throw error;
      return { error: null };
    } catch (error) {
      return { error: surfaceError('signOut', error) };
    }
  },

  async getCurrentUser() {
    try {
      const { data: { user }, error } = await supabase?.auth?.getUser();
      if (error) throw error;
      return { user, error: null };
    } catch (error) {
      return { user: null, error: surfaceError('getCurrentUser', error) };
    }
  },

  async getUserProfile(userId) {
    try {
      const { data, error } = await supabase?.from('user_profiles')?.select('*')?.eq('id', userId)?.maybeSingle();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error: surfaceError('getUserProfile', error) };
    }
  },

  async updateUserProfile(userId, updates) {
    try {
      const { data, error } = await supabase?.from('user_profiles')?.update(updates)?.eq('id', userId)?.select()?.maybeSingle();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error: surfaceError('updateUserProfile', error) };
    }
  }
};
